import React from 'react';
import { SolutionTemplate } from '../../components/SolutionTemplate';

export const CommercialSolar = () => {
  const benefits = [
    { title: 'Reduce Operating Costs', description: 'Cut electricity expenses by 60-80%. Savings flow directly to bottom line.' },
    { title: 'Tax Benefits', description: 'Claim 40% accelerated depreciation on solar assets under Income Tax Act.' },
    { title: 'Brand Value', description: 'Showcase corporate sustainability commitment. Improve ESG scores and attract conscious customers.' },
    { title: 'Hedge Against Tariff Hikes', description: 'Lock in energy costs for 25 years. Protect against 8-10% annual tariff increases.' },
    { title: 'Quick ROI', description: 'Typical payback period of 3-4 years for commercial installations.' },
    { title: 'Low Maintenance', description: 'Minimal downtime. Remote monitoring ensures peak performance.' }
  ];

  const features = [
    { title: 'High-Efficiency Panels', description: 'Industrial-grade monocrystalline panels rated for harsh conditions' },
    { title: 'Commercial Inverters', description: 'String or central inverters with 97%+ efficiency' },
    { title: 'Heavy-Duty Structures', description: 'Engineered for industrial roofs and ground mount applications' },
    { title: 'Advanced Monitoring', description: 'SCADA systems with real-time generation analytics' }
  ];

  const faqs = [
    { question: 'What size system do I need for my business?', answer: 'It depends on your sanctioned load and monthly consumption. For a factory consuming 5,000 units/month, a 30-40 kW system is typical. Our engineers conduct energy audits to recommend optimal sizing.' },
    { question: 'Can I get financing for commercial solar?', answer: 'Yes. Most banks offer solar loans at 9-11% interest for up to 10 years. IREDA and NABARD provide priority lending for renewable energy projects.' },
    { question: 'How does accelerated depreciation work?', answer: 'Under the Income Tax Act, you can claim 40% depreciation on solar assets in the first year, significantly reducing tax liability. Consult your CA for eligibility.' },
    { question: 'Will installation disrupt operations?', answer: 'Minimal disruption. Installation happens on rooftops or unused land. We work in phases to ensure business continuity.' }
  ];

  return (
    <SolutionTemplate
      title="Commercial Solar Solutions"
      description="Reduce operational costs, improve cash flow, and demonstrate environmental leadership. Ideal for offices, factories, hospitals, schools, and warehouses."
      heroImage="https://images.unsplash.com/photo-1726866492047-7f9516558c6e"
      benefits={benefits}
      features={features}
      faqs={faqs}
      testId="commercial-solar"
    />
  );
};

export const HousingSocietySolar = () => {
  const benefits = [
    { title: 'Reduce Common Area Bills', description: 'Society maintenance charges drop by 30-50% as common area electricity costs reduce.' },
    { title: 'Increase Property Value', description: 'Solar-equipped societies command premium prices and attract quality residents.' },
    { title: 'Governance Support', description: 'We handle all RWA approvals, member communications, and DISCOM coordination.' },
    { title: 'Flexible Models', description: 'CAPEX, RESCO, or community ownership models available based on society preference.' },
    { title: 'Member Benefits', description: 'Individual members can opt for virtual net metering to offset their flat\'s consumption.' },
    { title: 'Environmental Leadership', description: 'Position your society as eco-conscious and reduce collective carbon footprint.' }
  ];

  const features = [
    { title: 'Society-Scale Design', description: 'Sized for common areas (lifts, lights, pumps) or entire society load' },
    { title: 'Virtual Net Metering', description: 'Credits distributed among members based on their share' },
    { title: 'Central Monitoring', description: 'Society management can track generation and savings centrally' },
    { title: 'Member Portal', description: 'Individual members see their share of solar benefits' }
  ];

  const faqs = [
    { question: 'Do we need approval from all members?', answer: 'Typically, a simple majority (51%) in a General Body Meeting is sufficient. We provide presentation templates and financial projections to help secure approval.' },
    { question: 'How is the cost split among members?', answer: 'Usually proportional to flat area or per-flat basis. Society decides the model democratically. Payment can be one-time or via monthly maintenance increase.' },
    { question: 'What is virtual net metering?', answer: 'Allows solar generation from common rooftop to be credited to individual member meters. Each flat gets a proportional share of solar units, reducing their individual bills.' },
    { question: 'Who maintains the system?', answer: 'We provide 5-year comprehensive maintenance. Post that, society can extend AMC or self-maintain (very minimal work required).' }
  ];

  return (
    <SolutionTemplate
      title="Housing Society Solar Solutions"
      description="Transform your RWA with solar. Reduce maintenance costs, increase property value, and lead the way in community sustainability."
      heroImage="https://images.unsplash.com/photo-1715596636360-c47539cbfbbe"
      benefits={benefits}
      features={features}
      faqs={faqs}
      testId="housing-society-solar"
    />
  );
};

export const OnGridSolar = () => {
  const benefits = [
    { title: 'Best ROI', description: 'Lowest cost per watt and fastest payback period (3-5 years) among all solar system types.' },
    { title: 'Net Metering', description: 'Sell excess power back to grid. Your meter runs backward when you generate more than you consume.' },
    { title: 'No Battery Costs', description: 'Grid acts as infinite battery. No recurring battery replacement expenses.' },
    { title: 'Subsidy Eligible', description: 'Residential on-grid systems qualify for up to ₹78,000 government subsidy.' },
    { title: 'Simple Maintenance', description: 'Fewer components mean less that can go wrong. Just panels, inverter, and net meter.' },
    { title: 'Scalable', description: 'Easy to expand capacity later by adding more panels as your consumption grows.' }
  ];

  const features = [
    { title: 'Grid-Tie Inverter', description: 'Synchronizes with grid frequency and voltage automatically' },
    { title: 'Bi-Directional Meter', description: 'Tracks import from and export to grid separately' },
    { title: 'Anti-Islanding Protection', description: 'System shuts down during grid failure for lineman safety' },
    { title: 'Net Metering Agreement', description: 'Legal framework for selling power back to DISCOM' }
  ];

  const faqs = [
    { question: 'What happens to excess electricity I generate?', answer: 'It\'s fed into the grid via net metering. Your meter records export units. At month-end, export units are subtracted from import units. If you\'re a net exporter, credits roll over (policies vary by state).' },
    { question: 'Will I have power during outages?', answer: 'No. On-grid inverters shut down when grid fails (anti-islanding protection) for safety of utility workers. If backup is critical, consider a hybrid system with battery.' },
    { question: 'How long does net metering approval take?', answer: 'Typically 2-4 weeks after application. We handle the entire process including technical meetings with DISCOM officials.' },
    { question: 'Is my electricity bill zero after installing solar?', answer: 'Not quite. You\'ll still pay fixed charges and meter rent (₹100-200/month). Your consumption charges drop to near-zero if system is sized correctly.' }
  ];

  return (
    <SolutionTemplate
      title="On-Grid Solar Systems"
      description="Connected to the grid with net metering. Highest savings potential and fastest ROI. Perfect for areas with stable grid supply."
      heroImage="https://images.unsplash.com/photo-1761472823286-9f6093ed6663"
      benefits={benefits}
      features={features}
      faqs={faqs}
      testId="on-grid-solar"
    />
  );
};

export const OffGridSolar = () => {
  const benefits = [
    { title: 'Complete Independence', description: 'Zero reliance on grid. Generate, store, and use your own power 24/7.' },
    { title: 'Remote Area Viable', description: 'Perfect for locations with no grid access or unreliable supply.' },
    { title: 'No Electricity Bills', description: 'Ever. Your only costs are system maintenance and eventual battery replacement.' },
    { title: 'Critical Load Backup', description: 'Never worry about power cuts affecting essential equipment or operations.' },
    { title: 'Environmentally Clean', description: 'Replace diesel generators with silent, emission-free solar power.' },
    { title: 'Future-Proof', description: 'Protected from grid infrastructure failures and transmission losses.' }
  ];

  const features = [
    { title: 'Battery Bank', description: 'Lithium or lead-acid batteries sized for 1-3 days of backup' },
    { title: 'Off-Grid Inverter', description: 'Creates its own grid, manages charging and discharging automatically' },
    { title: 'Charge Controller', description: 'MPPT technology ensures maximum power extraction and battery longevity' },
    { title: 'Generator Integration', description: 'Option to add diesel/petrol genset for extended cloudy periods' }
  ];

  const faqs = [
    { question: 'How many days of backup can batteries provide?', answer: 'Typically 1-2 days of full-load backup. For extended autonomy (3-5 days), battery bank size and cost increase significantly. Most systems are designed for overnight + 1 cloudy day.' },
    { question: 'What type of batteries should I choose?', answer: 'Lithium (LiFePO4): 10+ year life, 90%+ efficiency, but higher cost (₹18,000-25,000 per kWh). Lead-acid: 5-7 year life, 80% efficiency, lower cost (₹8,000-12,000 per kWh). Lithium recommended for longer-term value.' },
    { question: 'Can I add grid connection later?', answer: 'Yes. Most off-grid inverters can be configured for hybrid mode. You\'ll need to add grid integration hardware and net metering, but the core system (panels, batteries) remains the same.' },
    { question: 'What happens during extended monsoon?', answer: 'System should be oversized by 20-30% for monsoon. Alternatively, add a small genset as backup. Advanced systems use AI to predict weather and manage loads intelligently.' }
  ];

  return (
    <SolutionTemplate
      title="Off-Grid Solar Systems"
      description="Complete energy independence with battery storage. Perfect for remote areas, farmhouses, or locations with frequent power cuts."
      heroImage="https://images.pexels.com/photos/8853525/pexels-photo-8853525.jpeg"
      benefits={benefits}
      features={features}
      faqs={faqs}
      testId="off-grid-solar"
    />
  );
};
